import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Edit, Trash2, Search, AlertCircle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../ui/dialog';
import { UFType, getAllUFTypes, createUFType, updateUFType, deleteUFType } from '../../services/ufTypeService';
import { showSuccess } from '../Alerts';
import { handleLoadError, handleApiError, handleDeleteError } from '../../utils/errorHandler';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../RequirePermission';
import { LABEL_COLORS, DEFAULT_TYPE_COLOR, getColorClasses } from './labelColors';

const UnitTypesTab: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [unitTypes, setUnitTypes] = useState<UFType[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [editingType, setEditingType] = useState<UFType | null>(null);
    const [typeToDelete, setTypeToDelete] = useState<UFType | null>(null);
    const [formData, setFormData] = useState({ name: '', icon: DEFAULT_TYPE_COLOR });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [, setIsLoading] = useState(false);

    useEffect(() => {
        loadUFTypes();
    }, []);

    const loadUFTypes = async () => {
        try {
            setIsLoading(true);
            const data = await getAllUFTypes();
            setUnitTypes(data);
        } catch (error) {
            handleLoadError(error, 'los tipos de unidades funcionales');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredTypes = unitTypes.filter(type =>
        type.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};
        if (!formData.name.trim()) {
            errors.name = 'El nombre es obligatorio';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setIsLoading(true);
            if (editingType) {
                await updateUFType(editingType.id, { name: formData.name, icon: formData.icon });
            } else {
                await createUFType({ name: formData.name, icon: formData.icon });
            }

            await loadUFTypes();
            showSuccess(`Tipo de unidad ${editingType ? 'actualizado' : 'creado'} exitosamente`);
            setIsDialogOpen(false);
            setEditingType(null);
            setFormData({ name: '', icon: DEFAULT_TYPE_COLOR });
            setFormErrors({});
        } catch (error) {
            handleApiError(error, `Error al ${editingType ? 'actualizar' : 'crear'} el tipo de unidad`);
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!typeToDelete) return;

        try {
            setIsLoading(true);
            await deleteUFType(typeToDelete.id);
            await loadUFTypes();
            showSuccess('Tipo de unidad eliminado exitosamente');
            setIsDeleteDialogOpen(false);
            setTypeToDelete(null);
        } catch (error) {
            handleDeleteError(error, 'el tipo de unidad');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-0">
            {/* Toolbar */}
            <div className="flex flex-col items-end gap-4 px-6 py-4 border-b md:flex-row border-border">
                <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditingType(null);
                            setFormData({ name: '', icon: DEFAULT_TYPE_COLOR });
                            setFormErrors({});
                            setIsDialogOpen(true);
                        }}
                        className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                    >
                        <Plus className="w-4 h-4" />
                        Nuevo Tipo
                    </Button>
                </RequirePermission>
                <div className="w-full space-y-2 md:w-72 md:ml-auto">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Buscar Tipo</label>
                    <div className="relative">
                        <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Buscar tipos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {filteredTypes.length === 0 ? (
                    <div className="py-12 text-center">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-muted flex items-center justify-center">
                            <Plus className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <h3 className="mb-2 text-lg font-medium text-foreground">No hay tipos de unidad</h3>
                        <p className="text-sm text-muted-foreground">Crea el primer tipo de unidad funcional.</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border bg-muted/50">
                                <th className="px-5 py-3 text-left">
                                    <span className="text-sm font-medium text-muted-foreground">Tipo de Unidad</span>
                                </th>
                                <th className="px-5 py-3 text-left">
                                    <span className="text-sm font-medium text-muted-foreground">Usos</span>
                                </th>
                                <th className="px-5 py-3 text-center">
                                    <span className="text-sm font-medium text-muted-foreground">Acciones</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTypes.map((type, index) => {
                                const isLast = index === filteredTypes.length - 1;
                                const cellClasses = isLast ? 'px-5 py-3' : 'px-5 py-3 border-b border-border';
                                const color = getColorClasses(type.icon);
                                return (
                                    <tr key={type.id} className="transition-colors hover:bg-muted/30">
                                        <td className={cellClasses}>
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${color.bg} ${color.text}`}>
                                                {type.name}
                                            </span>
                                        </td>
                                        <td className={cellClasses}>
                                            <span className="text-sm text-muted-foreground">
                                                {type.usages} {type.usages === 1 ? 'uso' : 'usos'}
                                            </span>
                                        </td>
                                        <td className={`${cellClasses} text-center`}>
                                            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                                                <div className="flex items-center justify-center gap-0.5">
                                                    <button
                                                        onClick={() => {
                                                            setEditingType(type);
                                                            setFormData({ name: type.name, icon: type.icon || DEFAULT_TYPE_COLOR });
                                                            setFormErrors({});
                                                            setIsDialogOpen(true);
                                                        }}
                                                        className="inline-flex items-center justify-center p-1.5 transition-colors rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground"
                                                        title="Editar"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setTypeToDelete(type);
                                                            setIsDeleteDialogOpen(true);
                                                        }}
                                                        className="inline-flex items-center justify-center p-1.5 transition-colors rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </RequirePermission>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {filteredTypes.length > 0 && (
                <div className="flex items-center px-6 py-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                        Mostrando {filteredTypes.length} tipo{filteredTypes.length !== 1 ? 's' : ''}
                    </p>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="max-w-md bg-card border-border">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
                                {editingType ? <Edit className="w-5 h-5 text-indigo-600" /> : <Plus className="w-5 h-5 text-indigo-600" />}
                                {editingType ? 'Editar Tipo de Unidad' : 'Nuevo Tipo de Unidad'}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="grid gap-5 py-4">
                            {/* Nombre */}
                            <div className="space-y-2">
                                <Label htmlFor="typeName" className="text-sm font-medium text-foreground">
                                    Nombre del Tipo <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="typeName"
                                    value={formData.name}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, name: e.target.value }));
                                        setFormErrors(prev => ({ ...prev, name: '' }));
                                    }}
                                    className={`bg-background border-input ${formErrors.name ? 'border-destructive' : ''}`}
                                    placeholder="Ej: Monoambiente, 3 Dormitorios, Local Comercial..."
                                />
                                {formErrors.name && (
                                    <p className="text-xs text-destructive">{formErrors.name}</p>
                                )}
                            </div>

                            {/* Selector de color */}
                            <div className="space-y-3">
                                <Label className="text-sm font-medium text-foreground">Color de la etiqueta</Label>

                                {/* Preview */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">Vista previa:</span>
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getColorClasses(formData.icon).bg} ${getColorClasses(formData.icon).text}`}>
                                        {formData.name || 'Nombre del tipo'}
                                    </span>
                                </div>

                                {/* Paleta */}
                                <div className="flex flex-wrap gap-2">
                                    {LABEL_COLORS.map((color) => (
                                        <button
                                            key={color.id}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, icon: color.id }))}
                                            title={color.id}
                                            className={`w-7 h-7 rounded-full transition-all focus:outline-none ${
                                                formData.icon === color.id
                                                    ? 'ring-2 ring-offset-2 ring-foreground scale-110'
                                                    : 'hover:scale-105 opacity-80 hover:opacity-100'
                                            }`}
                                            style={{ backgroundColor: color.dot }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsDialogOpen(false)}
                                className="border-input hover:bg-accent text-foreground"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSave}
                                className="text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                                {editingType ? 'Actualizar' : 'Guardar'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </RequirePermission>

            {/* Delete Confirmation Dialog */}
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <DialogContent className="rounded-lg bg-card border-border">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-foreground">
                                <AlertCircle className="w-5 h-5 text-destructive" />
                                Confirmar Eliminación
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                ¿Estás seguro de que deseas eliminar el tipo "{typeToDelete?.name}"?
                                {typeToDelete && typeToDelete.usages > 0 && (
                                    <span className="block mt-2 text-amber-600">
                                        Este tipo está siendo usado por {typeToDelete.usages} unidades.
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => { setIsDeleteDialogOpen(false); setTypeToDelete(null); }}
                                className="rounded-lg"
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={confirmDelete}
                                className="rounded-lg bg-destructive text-destructive-foreground hover:opacity-90"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </RequirePermission>
        </div>
    );
};

export default UnitTypesTab;
